import { NormalizedOrder, OrdersResponse } from '../types/unwinding.types';

const emptyOrdersSummary = {
  kalshi: { count: 0, totalVolume: 0 },
  polymarket: { count: 0, totalVolume: 0 },
};

const toNumber = (value: unknown, fallback = 0): number => {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const toIsoDate = (value: unknown): string => {
  if (typeof value === 'string' || typeof value === 'number') {
    const d = new Date(value);
    if (!Number.isNaN(d.getTime())) return d.toISOString();
  }
  return new Date().toISOString();
};

const normalizeSide = (raw: any): 'yes' | 'no' => {
  const side = String(raw?.side ?? '').toLowerCase();
  if (side === 'yes' || side === 'buy') return 'yes';
  if (side === 'no' || side === 'sell') return 'no';
  return 'yes';
};

const normalizeAction = (raw: any): 'buy' | 'sell' => {
  const action = String(raw?.action ?? '').toLowerCase();
  if (action === 'buy' || action === 'sell') return action;
  const side = String(raw?.side ?? '').toLowerCase();
  if (side === 'buy') return 'buy';
  if (side === 'sell') return 'sell';
  return 'buy';
};

const normalizePlatform = (raw: any): 'kalshi' | 'polymarket' => {
  const platform = String(raw?.platform ?? '').toLowerCase();
  if (platform === 'kalshi' || platform === 'polymarket') return platform;
  if (raw?.ticker || raw?.market_ticker || raw?.yes_price_dollars) return 'kalshi';
  return 'polymarket';
};

const normalizeOrder = (raw: any, index: number): NormalizedOrder => {
  const platform = normalizePlatform(raw);
  const quantity = toNumber(
    raw?.quantity ?? raw?.count ?? raw?.size ?? raw?.initial_count_fp ?? raw?.original_size
  );
  const filledQuantity = toNumber(
    raw?.filledQuantity ?? raw?.filled_quantity ?? raw?.fill_count_fp ?? raw?.size_matched ?? raw?.filledSize
  );
  const remainingQuantity = toNumber(
    raw?.remainingQuantity ?? raw?.remaining_quantity ?? raw?.remaining_count_fp,
    Math.max(quantity - filledQuantity, 0)
  );
  const price = toNumber(
    raw?.price ??
      raw?.yes_price_dollars ??
      (raw?.yes_price != null ? Number(raw.yes_price) / 100 : undefined)
  );

  return {
    id: String(raw?.id ?? raw?.order_id ?? raw?.orderId ?? `${platform}-${index}`),
    platform,
    marketId: String(
      raw?.marketId ??
        raw?.market_id ??
        raw?.ticker ??
        raw?.market_ticker ??
        raw?.tokenId ??
        raw?.token_id ??
        raw?.asset_id ??
        raw?.market ??
        ''
    ),
    side: normalizeSide(raw),
    action: normalizeAction(raw),
    price,
    quantity,
    filledQuantity,
    remainingQuantity,
    status: String(raw?.status ?? raw?.order_status ?? 'resting').toLowerCase(),
    isUnwinding: Boolean(raw?.isUnwinding ?? raw?.is_unwinding),
    ruleId: raw?.ruleId ?? raw?.rule_id,
    createdAt: toIsoDate(raw?.createdAt ?? raw?.created_at ?? raw?.created_time ?? raw?.timestamp),
    updatedAt: toIsoDate(
      raw?.updatedAt ??
        raw?.updated_at ??
        raw?.last_update_time ??
        raw?.timestamp ??
        raw?.createdAt ??
        raw?.created_at ??
        raw?.created_time
    ),
  };
};

const normalizeOrdersList = (raw: unknown): NormalizedOrder[] => {
  if (!Array.isArray(raw)) return [];
  return raw.map((o, i) => normalizeOrder(o, i));
};

const computeSummary = (orders: NormalizedOrder[]): OrdersResponse['summary'] => ({
  kalshi: {
    count: orders.filter((o) => o.platform === 'kalshi').length,
    totalVolume: orders
      .filter((o) => o.platform === 'kalshi')
      .reduce((sum, o) => sum + (o.price || 0) * (o.quantity || 0), 0),
  },
  polymarket: {
    count: orders.filter((o) => o.platform === 'polymarket').length,
    totalVolume: orders
      .filter((o) => o.platform === 'polymarket')
      .reduce((sum, o) => sum + (o.price || 0) * (o.quantity || 0), 0),
  },
});

export function normalizeOrdersPayload(raw: unknown): OrdersResponse {
  if (Array.isArray(raw)) {
    const orders = normalizeOrdersList(raw);
    return { orders, summary: computeSummary(orders) };
  }

  const candidate = (raw ?? {}) as Record<string, unknown>;
  const directOrders = normalizeOrdersList(candidate.orders);
  if (directOrders.length > 0 || Array.isArray(candidate.orders)) {
    const summary = candidate.summary && typeof candidate.summary === 'object'
      ? (candidate.summary as OrdersResponse['summary'])
      : computeSummary(directOrders);
    return { orders: directOrders, summary };
  }

  const kalshiOrders = normalizeOrdersList(candidate.kalshiOrders ?? candidate.kalshi).map((o) => ({
    ...o,
    platform: 'kalshi' as const,
  }));
  const polymarketOrders = normalizeOrdersList(
    candidate.polymarketOrders ?? candidate.polymarket
  ).map((o) => ({
    ...o,
    platform: 'polymarket' as const,
  }));
  const merged = [...kalshiOrders, ...polymarketOrders];
  if (merged.length > 0) {
    return { orders: merged, summary: computeSummary(merged) };
  }

  return { orders: [], summary: emptyOrdersSummary };
}
